<script lang="ts" setup>
import FilterTable from "@/components/Common/Table/FilterTable.vue";
import { ref } from "vue";
import type { Header } from "vue3-easy-data-table";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import Swal from "sweetalert2";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import { useShipManagementCompanyStore } from "@/stores/master/ship-management-company";
import { hasAccess } from "@/utils/helper";

const linkAdd = {
  text: "Add Ship Management Company",
  url: "/master/ship-management-company/create",
};

const headers: Header[] = [
  { text: "NAME", value: "name" },
  { text: "REGISTRATION NUMBER", value: "reg_no" },
  { text: "COUNTRY", value: "country" },
  { text: "ACTIONS", value: "action" },
];

const page = ref<number>(1);
const limit = ref<number>(10);
const totalItems = ref<number>(0);
const listCompanies = ref<any>([]);
const shipManagementCompanyStore = useShipManagementCompanyStore();
const isLoading = ref(false);

const loadDataShipManagementCompany = async (query = "") => {
  isLoading.value = true;
  const { success, message } = await shipManagementCompanyStore.dispatchGetList(
    page.value,
    limit.value,
    query
  );
  if (success) {
    listCompanies.value = await shipManagementCompanyStore.companies.items;
    page.value = await shipManagementCompanyStore.companies.page;
    limit.value = await shipManagementCompanyStore.companies.pageSize;
    totalItems.value = await shipManagementCompanyStore.companies.total;
  }
  isLoading.value = false;
};

const handleServerOptionsUpdate = (options: {
  page: number;
  rowsPerPage: number;
}) => {
  page.value = options.page;
  limit.value = options.rowsPerPage;
  loadDataShipManagementCompany();
};

const handleEdit = (company: any) => {
  router.push("/master/ship-management-company/" + company);
};

const handleDelete = async (company: any) => {
  const swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      cancelButton: "btn btn-danger",
      confirmButton: "btn btn-success",
    },
    buttonsStyling: false,
  });
  swalWithBootstrapButtons
    .fire({
      title: `Are you sure want to delete data ${company.name} ?`,
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    })
    .then(async (result) => {
      if (result.isConfirmed) {
        const { success, message } =
          await shipManagementCompanyStore.dispatchDelete(company.id);
        if (success) {
          swalWithBootstrapButtons.fire({
            title: "Deleted!",
            text: "Your file has been deleted.",
            icon: "success",
          });
          loadDataShipManagementCompany();
        } else {
          toast.error(message);
        }
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        swalWithBootstrapButtons.fire({
          title: "Cancelled",
          text: "Your action has been aborted",
          icon: "error",
        });
      }
    });
};

loadDataShipManagementCompany();
</script>
<template>
  <div class="card bg-white border-0 rounded-3 mb-4">
    <div class="card-body p-4">
      <div
        class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
      >
        <h3 class="mb-0">List Data Ship Management Company</h3>
      </div>
      <FilterTable
        :link="linkAdd"
        @searchByQuery="loadDataShipManagementCompany"
      />
      <div class="table-content">
        <BaseLoading v-if="isLoading" />
        <EasyDataTable
          :headers="headers"
          :items="listCompanies"
          v-if="!isLoading"
          show-index
          :server-options="{
            page: page,
            rowsPerPage: limit,
          }"
          :server-items-length="totalItems"
          @update:server-options="handleServerOptionsUpdate"
          buttons-pagination
        >
          <template #item-action="props">
            <div class="d-flex gap-2">
              <button
                v-if="hasAccess('ship_management_company-update')"
                class="btn btn-info btn-sm text-white"
                @click="handleEdit(props.id)"
              >
                Edit
              </button>
              <button
                v-if="hasAccess('ship_management_company-delete')"
                class="btn btn-danger btn-sm"
                @click="handleDelete(props)"
              >
                Delete
              </button>
            </div>
          </template>
        </EasyDataTable>
      </div>
    </div>
  </div>
</template>
